import React, { useCallback } from "react";
import {
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import {
  Box,
  Link,
  Skeleton,
  Stack,
  Typography,
} from "@mui/material";
import { Image } from "mui-image";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';

import ImageCarousel from "../Carousels/ImageCarouel";
import ProductIcon from "../Icons/product";
import { useTraceData } from "../../context/TraceDataContext";
import { TimelineStyles } from "./styles";
import IconLabel from "../UI/IconLabel";
import { NOT_AVAILABLE } from "../../constants";
import { getProductNames, getTimeText } from "../../utils";


const ProductText = ({
  text,
  eventId,
  isProductLoading,
}: {
  text: string;
  eventId: string;
  isProductLoading: (eventId: string) => boolean;
}) => {
  const isCurrentlyLoading = isProductLoading(eventId);

  if (isCurrentlyLoading) {
    return <Box sx={{ height: 20 }} />
  }

  if (!text) return null;

  return (
    <IconLabel
      icon={<ProductIcon sx={TimelineStyles.productIcon} />}
      tooltip="Products"
      sx={{ minHeight: 20 }}
    >
      {(() => {
        const parts = text.split(', +');
        if (parts.length === 1) return text;
        return (
          <>

            <Typography component="span" variant="caption" sx={{ color: '#005bb5' }}>
              , +{parts[1]} products
            </Typography>
            {parts[0]}
          </>
        );
      })()}
    </IconLabel>
  );
};

const EventImageBlock = ({
  image,
  freshnessMeterDetails,
  eventId,
  isEventLoading,
}: {
  loading: boolean;
  image?: string | null;
  freshnessMeterDetails?: any;
  eventId: string;
  isEventLoading: (eventId: string) => boolean;
}) => {
  const isCurrentlyLoading = isEventLoading(eventId);

  if (isCurrentlyLoading) {
    return <Skeleton variant="rectangular" width="100%" height={260} />;
  }

  return (
    <ImageCarousel
      images={image ? [{ src: image, title: "Event Image" }] : []}
      freshnessMeterDetails={freshnessMeterDetails}
      loading={isCurrentlyLoading}
    />
  );
};

interface TimeLineProps {
  traces: any[];
  isLastInTimeline?: boolean;
  isLastInDisplay?: boolean;
  isGrouped?: boolean;
  groupedTraces?: any[];
  onMoreDetails?: (payload: { eventData: any | any[]; productDetails: Record<string, any> }) => void;
}

const TimeLine = ({
  traces,
  isLastInTimeline = false,
  isLastInDisplay = false,
  isGrouped = false,
  groupedTraces = [],
  onMoreDetails,
}: TimeLineProps) => {

  const {
    productDetails,
    isProductLoading,
    eventImages,
    isEventLoading,
    registerRef,
  } = useTraceData();

  const handleMoreDetails = useCallback(
    (data: any | any[]) => {
      const items = Array.isArray(data) ? data : [data];
      const productDetailsMap: Record<string, any> = {};
      items.forEach((t) => {
        const id = t?.eventID;
        if (id) {
          productDetailsMap[id] = productDetails?.[id] || null;
        }
      });
      onMoreDetails?.({ eventData: data, productDetails: productDetailsMap });
    },
    [onMoreDetails, productDetails]
  );

  return (
    <>
      {traces.map((item) => {
        const eventId = item.eventID;

        const firstItem = isGrouped ? groupedTraces[0] : item;
        const lastItem =
          isGrouped && groupedTraces.length > 1
            ? groupedTraces[groupedTraces.length - 1]
            : null;

        const eventImage = eventImages?.[eventId];

        const productNames = getProductNames(isGrouped, groupedTraces, eventId, productDetails);
        const timeText = getTimeText(isGrouped, firstItem, lastItem);

        return (
          <TimelineItem
            key={eventId}
            sx={{ pt: 0, mt: 0 }}
            ref={(el) =>
              eventId &&
              registerRef(
                eventId,
                item?.outputProductInstances || [],
                el as HTMLDivElement
              )
            }
          >
            {/* LEFT CONTENT */}
            <TimelineOppositeContent sx={{ width: { xs: "95%", md: "200px" }, py: 1, px: 0 }}>
              <Stack alignItems="flex-end" textAlign="right">
                <Typography variant="subtitle2" fontWeight="bold" pb={1}>
                  {firstItem.eventDescription}
                </Typography>

                <IconLabel
                  icon={<CalendarMonthOutlinedIcon sx={TimelineStyles.icon} />}
                  tooltip="Event Time"
                  sx={firstItem?.location === NOT_AVAILABLE ? { mb: 1 } : { mb: 0 }}
                >
                  {timeText}
                </IconLabel>

                {firstItem?.location !== NOT_AVAILABLE && (
                  <IconLabel
                    icon={<LocationOnOutlinedIcon sx={TimelineStyles.icon} />}
                    tooltip="Event Location"
                    sx={{ py: 1 }}
                  >
                    <Typography variant="caption" mt={0.5}>
                      {(() => {
                        if (isGrouped) {
                          // For grouped events, collect all unique locations
                          const uniqueLocations = Array.from(
                            new Set(groupedTraces.map(trace => trace.location).filter(Boolean))
                          );
                          if (uniqueLocations.length === 1) {
                            return uniqueLocations[0]; // Show single location if all are same
                          }
                          return (
                            <>
                              {uniqueLocations[0]}
                              <Typography component="div" variant="caption" sx={{ color: '#005bb5' }}>
                                +{uniqueLocations.length - 1} locations
                              </Typography>
                            </>
                          );
                        } else {
                          return firstItem.location;
                        }
                      })()}
                    </Typography>
                  </IconLabel>
                )}

                <ProductText
                  text={productNames}
                  eventId={eventId}
                  isProductLoading={isProductLoading}
                />

                <Link
                  component="button"
                  variant="caption"
                  sx={TimelineStyles.moreLink}
                  onClick={() =>
                    handleMoreDetails(isGrouped ? groupedTraces : item)
                  }
                >
                  More Details <OpenInNewOutlinedIcon sx={{ fontSize: "12px !important", ml: -0.2, }} />
                </Link>
              </Stack>
            </TimelineOppositeContent>

            {/* TIMELINE DOT */}
            <TimelineSeparator>
              <TimelineDot
                sx={{
                  p: 0,
                  m: 1,
                  border: "none",
                  boxShadow: "none",
                  backgroundColor: "white",
                }}
              >
                <Image
                  src={
                    isLastInTimeline
                      ? "/images/Tralexho-X.png"
                      : "/images/Tralexho-O.png"
                  }
                  style={{ width: 25 }}
                  alt="tralexho"
                />
              </TimelineDot>
              {!isLastInDisplay && (
                <TimelineConnector sx={{ backgroundColor: "#000" }} />
              )}
            </TimelineSeparator>

            {/* RIGHT CONTENT */}
            <TimelineContent sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Box sx={{ width: 180, mt: 2, textAlign: "right", ml: "auto", cursor: "pointer" }}
                onClick={() => { !item?.freshnessMeterDetails && handleMoreDetails(isGrouped ? groupedTraces : item) }
                }>
                <EventImageBlock
                  loading={false}
                  image={eventImage}
                  freshnessMeterDetails={item.freshnessMeterDetails}
                  eventId={eventId}
                  isEventLoading={isEventLoading}
                />
              </Box>
            </TimelineContent>
          </TimelineItem>
        );
      })}
    </>
  );
};

export default TimeLine;
